/**
 * BoardInputController
 * Handles logic for input on board of tiles, decides when to select or unselect tiles.
 * Decides when word is confirmed by user both by tap/click or by swipe/mouse move.
 */

export default class BoardInputController {

    // ==================== Construction ====================
    constructor(board) {
        this.board = board;

        // Enable input on back image and add all needed listeners
        this.board.backPicture.inputEnabled = true;
        this.board.backPicture.events.onInputDown.add(this.onDown, this);
        this.board.backPicture.events.onInputUp.add(this.onUp, this);
        this.board.game.input.addMoveCallback(this.onMove, this);

        // Handles all selected tiles
        this.selected = [];

        // useful to avoid situation where mouse is released outside, then go back to tiles
        // and tiles are selected, or start selecting while the whole system is not active
        this.canSelectOnMove = false;

        // used to detect word confirmation at the and of swipe (tap requires additional confirmation
        // by tap on lastly selected tile)
        this.selectedWithMove = 0;

        this.active = true; // quick flag to turn on/off selecting possibility
        this.specialUnselectBlockForMove = false;
        this.lastSwipePosition = null;
        this.selectTilesCounter = 0
    }

    // ==================== Input Events ====================
    onDown(sprite, pointer) {
        if (!this.active) { return; }
        this.lastSwipePosition = {x: pointer.x, y: pointer.y};
        this.canSelectOnMove = true;
        var tile = this.getTileUnderPointer(pointer);
        if (tile && tile.canBeSelected()) { this.selectTile(tile); }
    }

    onUp() {
        // this.specialUnselectBlockForMove = false
        this.canSelectOnMove = false;
        this.lastSwipePosition = null;
        if (this.selectedWithMove) { this.onWordConfirmed(); }
        this.selectedWithMove = 0;
    }

    onMove(pointer) {
        if (!this.canSelectOnMove || !this.active) { return; }

        this.selectTilesCounter = 0;
        if (this.lastSwipePosition === null) {
            this.lastSwipePosition = {x: pointer.x, y: pointer.y};
            var tile = this.getTileUnderPointer(pointer);
            if (tile && tile.canBeSelected()) { this.selectTileOnMove(tile, pointer); }
        }
        else {
            var dx = pointer.x - this.lastSwipePosition.x;
            var dy = pointer.y - this.lastSwipePosition.y;
            var steps = Math.round(Math.max(Math.abs(dx), Math.abs(dy)));

            if (dx === 0 && dy === 0) {
                var tile = this.getTileUnderPointer(pointer);
                if (tile && tile.canBeSelected()) { this.selectTileOnMove(tile, pointer); }
            }
            else {
                var stepX = dx / steps;
                var stepY = dy / steps;
                while (steps) {
                    var tile = this.getTileUnderPointer(this.lastSwipePosition);
                    if (tile && tile.canBeSelected()) { this.selectTileOnMove(tile, this.lastSwipePosition); }
                    this.lastSwipePosition.x += stepX;
                    this.lastSwipePosition.y += stepY;
                    steps -= 1;
                }
            }
        }

        // var tile = this.getTileUnderPointer(pointer)
        // if (tile && tile.canBeSelected()) this.selectTileOnMove(tile, pointer)
        if (this.selectTilesCounter > 0) { AUDIO.playSound('tilePop') }
    }

    // ==================== Word Events ====================
    onWordChanged() {
        AUDIO.playSound('tilePop');
        var word = this.getSelectedWord();
        var valid = this.board.wordVerificator.isWordValid(word);
        var score = this.board.scoreCalculator.getScoreForWord(word);
        this.board.pathCreator.updatePath(valid);
        GS.onWordChanged(word, valid, score);
    }

    onWordChangedFromPowerup(text) {
        var word = this.getSelectedWord();
        var score = this.board.scoreCalculator.getScoreForWordSimple(word);
        GS.onWordChanged('BOMB', true, score);
    }

    onWordChangedOnMove() {
        // AUDIO.playSound('tilePop');
        this.selectTilesCounter += 1;
        var word = this.getSelectedWord();
        var valid = this.board.wordVerificator.isWordValid(word);
        var score = this.board.scoreCalculator.getScoreForWord(word);
        this.board.pathCreator.updatePath(valid);
        GS.onWordChanged(word, valid, score);
    }

    onWordCleared() {
        this.clearSelected();
        this.board.pathCreator.updatePath(false);
        GS.onWordChanged('', false, -90);
    }

    onWordConfirmed() {
        var word = this.getSelectedWord();
        var valid = this.board.wordVerificator.isWordValid(word);
        var score = this.board.scoreCalculator.getScoreForWord(word);

        if (valid) {
            this.board.explosionManager.explodeTiles(this.selected, this.board);
            this.board.pathCreator.hideConnections();

            this.active = false;
            this.selected = [];

            GS.onWordConfirmed(score, word);
            AUDIO.playSound('baddieGrunt');
        }
    }

    onWordConfirmedFromPowerup() {
        var word = this.getSelectedWord();
        var score = this.board.scoreCalculator.getScoreForWordSimple(word);

        this.board.explosionManager.explodeTiles(this.selected, this.board);
        this.board.pathCreator.hideConnections();

        this.active = false;
        this.selected = [];

        GS.onWordConfirmed(score, word);
        AUDIO.playSound('bombExplosion');
    }

    // ==================== Selection ====================
    selectTile(tile) {
        // Helper variable to detect later if word actually changed
        var lengthBefore = this.selected.length;

        // comparison of length isn't enough if we have one selected letter and then select the another one
        // so here we have additional helper
        var firstBuffer = null;
        if (lengthBefore === 1) {
            firstBuffer = this.selected[0];
        }

        if (this.selected.length === 0) {
            this.selected.push(tile);
        }
        else if (this.selected.length === 1 && tile === this.selected[0]) {
            this.clearSelected();
            this.onWordChanged();
            this.canSelectOnMove = false;
            return;
        }
        else if (tile.isSelected()) {
            this.clearAfterTile(tile);
        }
        else {
            var neighbour = this.isNeighbourOfLastTile(tile);
            if (!neighbour) { this.clearSelected(); } // If they are not neighbours start new selection
            this.selected.push(tile);
        }

        var lengthChanged = this.selected.length !== lengthBefore;
        var anotherOneSelected = lengthBefore === 1 && firstBuffer !== this.selected[0];
        if (lengthChanged || anotherOneSelected) {
            tile.select();
            this.onWordChanged();
        }
        // else if (!anotherOneSelected ) this.clearSelected();
        else { this.onWordConfirmed(); }
    }

    selectTileOnMove(tile, pointer) {
        if (!this.canSelectOnMove) { return; }
        var lengthBefore = this.selected.length;

        if (this.selected.length === 0) {
            // this should be commented if selecting on swipe after start on empty tile is not wanted
            this.selected.push(tile)
        }
        else if (tile.isSelected()) {
            // distance limit is needed to make diagonal selects possible
            var distLimit = tile.isWithinDistanceLimit(pointer);
            var isDiagonalTile = this.isDiagonalToLastTile(tile);
            if (!distLimit) { return; }
            this.clearAfterTile(tile);
        }
        else {
            // detect the situation where swipe is super-quick or goes through empty tiles
            var neighbour = this.isNeighbourOfLastTile(tile);
            distLimit = tile.isWithinDistanceLimit(pointer);
            var isDiagonalTile = this.isDiagonalToLastTile(tile);
            if (isDiagonalTile) { distLimit = true; }
            if (!neighbour || !distLimit) { return; }
            this.selected.push(tile);
        }

        if (this.selected.length !== lengthBefore) {
            tile.select();
            this.onWordChangedOnMove();
            this.selectedWithMove += 1;
        }
    }

    selectAround(tile) {
        for (var col = tile.col - 1; col <= tile.col + 1; col++) {
            for (var row = tile.row - 1; row <= tile.row + 1; row++) {
                if (col < 0 || row < 0 || col >= this.board.columnsCount || row >= this.board.rowsCount) {
                    continue;
                }

                var newTile = this.board.getTile(col, row);
                if (newTile === tile || !newTile.alive) { continue; }
                newTile.select();
                this.selected.push(newTile)
            }
        }

        tile.select();
        this.selected.push(tile);
    }

    // ==================== Clearing ====================
    clearSelected() {
        for (var i = 0; i < this.selected.length; i++) {
            this.selected[i].unselect();
        }
        this.selected = [];
    }

    clearAfterTile(tile) {
        for (var i = this.selected.length - 1; i >= 0; i--) {
            if (this.selected[i] === tile) { break; }
            this.selected[i].unselect();
            this.selected.pop();
        }
    }

    // ==================== Utility ====================
    isNeighbourOfLastTile(tile) {
        var lastTile = this.selected[this.selected.length - 1];
        var rowDiff = Math.abs(tile.row - lastTile.row);
        var colDiff = Math.abs(tile.col - lastTile.col);
        if (rowDiff > 1 || colDiff > 1) { return false; }
        return true;
    }

    isDiagonalToLastTile(tile) {
        var lastTile = this.selected[this.selected.length - 1];
        var rowDiff = Math.abs(tile.row - lastTile.row);
        var colDiff = Math.abs(tile.col - lastTile.col);
        if (rowDiff === 1 && colDiff === 1) { return true; }
        return false;
    }

    getTileUnderPointer(pointer) {
        var localPos = this.board.backPicture.toLocal(pointer);
        var col = Math.floor((localPos.x - 10 * PARAMS.scaleFactor) / 47 / PARAMS.scaleFactor);
        var row = Math.floor((localPos.y - 9 * PARAMS.scaleFactor) / 47 / PARAMS.scaleFactor);
        if (col < 0 || row < 0 || col >= this.board.columnsCount || row >= this.board.rowsCount) { return null; }
        return this.board.tiles[col][row];
    }

    getSelectedWord() {
        var word = '';
        for (var i = 0; i < this.selected.length; i++) {
            word += this.selected[i].symbol;
        }
        return word;
    }

    // ==================== Destruction ====================
    destroyInput() {
        this.lastSwipePosition = null;
        this.board.backPicture.events.onInputDown.removeAll();
        this.board.backPicture.events.onInputUp.removeAll();
        this.board.game.input.deleteMoveCallback(this.onMove, this);
    }
}
